import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { ChevronUp, Clock, Hourglass, ListChecks, Loader2, Lock, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { EscolhaMensagemAprovada } from './EscolhaMensagemAprovada';
import {
  custoDaMensagem, intencaoDe, liberado, primeiroNome, renderizarCorpo,
  useContatoDoCliente, useMensagensAprovadas, valoresIniciais, type MensagemAprovada,
} from './mensagensAprovadas';

// Composer da conversa de LINHA OFICIAL com a janela de 24h FECHADA.
// A lei (Douglas 11/08): pela Cloud API fora da janela, texto livre é aceito e
// DESCARTADO MUDO pela Meta — o consultor achava que mandou e nada chegava.
// Este composer substitui o campo de texto nesse estado: só deixa sair
// TEMPLATE APROVADO (que reabre a conversa de verdade). Quando o cliente
// responder, a janela reabre e o composer normal volta sozinho.
// A escolha da mensagem é a MESMA da Nova conversa (EscolhaMensagemAprovada).

export function ComposerTemplateOficial({
  conversationId,
  connectionId,
  telefone,
  cnpj,
  contatoNome,
  ultimaEntrada,
  ultimaSaida,
  onEnviado,
  onNotaInterna,
}: {
  conversationId: string;
  connectionId: string | null;
  telefone: string;
  cnpj?: string | null;
  contatoNome?: string | null;
  ultimaEntrada?: string | null;
  ultimaSaida?: string | null;
  onEnviado?: () => void;
  onNotaInterna?: () => void;
}) {
  const { profile } = useAuth();
  const [template, setTemplate] = useState<MensagemAprovada | null>(null);
  const [valores, setValores] = useState<Record<number, string>>({});
  const [enviando, setEnviando] = useState(false);
  const [listaAberta, setListaAberta] = useState(false);
  const [enviadaAgora, setEnviadaAgora] = useState<{ em: string; nome: string } | null>(null);

  // O horário da conversa chega atrasado na lista; a última mensagem de cada
  // lado vem direto das mensagens, para o quadro fechar assim que a nossa sai.
  const { data: ultimas } = useQuery({
    queryKey: ['composer-oficial-ultimas', conversationId],
    staleTime: 10_000,
    refetchInterval: 30_000,
    queryFn: async () => {
      const ult = async (direcao: 'inbound' | 'outbound') => {
        const { data } = await supabase
          .from('whatsapp_messages')
          .select('created_at')
          .eq('conversation_id', conversationId)
          .eq('direction', direcao)
          .order('created_at', { ascending: false })
          .limit(1);
        return (data?.[0] as { created_at?: string } | undefined)?.created_at ?? null;
      };
      const [entrada, saida] = await Promise.all([ult('inbound'), ult('outbound')]);
      return { entrada, saida };
    },
  });

  const { data: templates = [], isLoading } = useMensagensAprovadas(connectionId);
  const { data: contatoReal } = useContatoDoCliente(cnpj);

  const escolher = (t: MensagemAprovada) => {
    setTemplate(t);
    setValores(valoresIniciais(t, {
      cliente: primeiroNome(contatoReal) || primeiroNome(contatoNome),
      consultor: primeiroNome((profile as { nome?: string } | null)?.nome),
      empresa: String(contatoNome || ''),
      cnpj: cnpj || '',
    }));
  };

  // Depois de chamar o cliente pela oficial, o texto livre só volta quando ELE
  // responde. Dizer isso é o que faltou ao consultor em 25/09 (caso Luis/Ana Claudia).
  const aguardandoDesde = useMemo(() => {
    const ms = (v?: string | null) => (v ? new Date(v).getTime() : 0);
    const saida = Math.max(ms(ultimaSaida), ms(ultimas?.saida), ms(enviadaAgora?.em));
    const entrada = Math.max(ms(ultimaEntrada), ms(ultimas?.entrada));
    if (!saida || !(saida > entrada)) return null;
    const d = new Date(saida);
    const hoje = new Date().toDateString() === d.toDateString();
    const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    return hoje ? hora : `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${hora}`;
  }, [ultimaSaida, ultimaEntrada, ultimas, enviadaAgora]);

  const preview = template ? renderizarCorpo(template.corpo || '', valores) : '';
  const camposFaltando = (template?.variaveis || []).some((v) => !(valores[v.pos] || '').trim());

  const enviar = async () => {
    if (!template || camposFaltando || enviando) return;
    setEnviando(true);
    try {
      const params: Record<string, string> = {};
      for (const v of template.variaveis || []) params[String(v.pos)] = valores[v.pos] || '';
      const { data, error } = await supabase.functions.invoke('whatsapp-evolution-send', {
        body: {
          to: telefone,
          conversation_id: conversationId,
          connection_id: connectionId,
          text: preview,
          template_name: template.template_name,
          template_language: template.language || 'en',
          template_category: String(template.categoria || 'marketing').toLowerCase(),
          template_params: params,
        },
      });
      if (error || data?.success === false) {
        const context = error && 'context' in error ? error.context : null;
        const payload = context instanceof Response ? await context.clone().json().catch(() => null) : null;
        throw new Error(data?.erro_detalhe || data?.error || payload?.erro_detalhe || payload?.error || error?.message || 'Falha no envio do template');
      }
      const { data: auth } = await supabase.auth.getUser();
      const { error: erroRegistro } = await supabase.from('whatsapp_messages').insert({
        conversation_id: conversationId,
        direction: 'outbound',
        type: data?.template_como_texto ? 'text' : 'template',
        body: preview,
        status: 'sent',
        provider_message_id: data?.provider_message_id || null,
        sent_by_user_id: auth.user?.id || null,
        message_source: 'humano',
      });
      if (erroRegistro) toast.warning(`A mensagem saiu, mas o CRM não gravou quem enviou: ${erroRegistro.message}`);
      toast.success(data?.template_como_texto
        ? 'Enviado como mensagem normal — a janela estava aberta, sem custo de template.'
        : 'Mensagem aprovada enviada — quando o cliente responder, você escreve normalmente.');
      setEnviadaAgora({ em: new Date().toISOString(), nome: intencaoDe(template)?.apelido ?? template.nome_exibicao });
      setTemplate(null);
      setValores({});
      setListaAberta(false);
      onEnviado?.();
    } catch (e) {
      toast.error(`Não foi: ${(e as Error).message}`);
    } finally {
      setEnviando(false);
    }
  };

  const liberadas = templates.filter(liberado).length;

  const botaoNota = onNotaInterna && (
    <button
      type="button"
      onClick={onNotaInterna}
      className="inline-flex h-7 flex-shrink-0 items-center gap-1 rounded-md border border-border-warm bg-card px-2 text-[11px] font-semibold text-muted-foreground transition-colors hover:border-[#5B0A0D]/40 hover:text-[#5B0A0D] dark:hover:text-[#C87484]"
    >
      <Lock className="h-3 w-3" />
      Nota interna
    </button>
  );

  const escolha = (
    <>
      <EscolhaMensagemAprovada
        mensagens={templates}
        carregando={isLoading}
        selecionadaId={template?.id || ''}
        onSelecionar={escolher}
        valores={valores}
        onValor={(pos, v) => setValores((s) => ({ ...s, [pos]: v }))}
      />
      {template && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10.5px] text-muted-foreground">{custoDaMensagem(template)}</span>
          <Button
            size="sm"
            className="h-8 gap-1.5 bg-[linear-gradient(135deg,#4A1320,#5B0A0D)] px-3 text-[12px] font-bold text-white hover:opacity-95 dark:bg-[linear-gradient(135deg,#9D2530,#6C2236)]"
            onClick={enviar}
            disabled={camposFaltando || enviando}
            title={camposFaltando ? 'Preencha os campos da mensagem' : 'Enviar mensagem aprovada'}
          >
            {enviando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Enviar
          </Button>
        </div>
      )}
    </>
  );

  if (aguardandoDesde) {
    return (
      <div className="space-y-2 border-t border-border-warm bg-[#FAF8F4] px-3 py-2.5 dark:bg-[#241A1A]">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-full bg-[#F4E9EA] text-[#5B0A0D] dark:bg-[#3A2226] dark:text-[#C87484]">
            <Hourglass className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[12.5px] font-semibold text-foreground">
              {enviadaAgora ? `"${enviadaAgora.nome}" enviada` : 'Mensagem aprovada enviada'} {aguardandoDesde.includes('/') ? 'em' : 'às'} {aguardandoDesde} · aguardando o cliente responder
            </p>
            <p className="truncate text-[11px] text-muted-foreground">
              Quando ele responder, o campo de texto volta e você escreve normalmente, sem custo.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setListaAberta((v) => !v)}
            className="inline-flex h-7 flex-shrink-0 items-center gap-1 rounded-md border border-border-warm bg-card px-2 text-[11px] font-semibold text-muted-foreground transition-colors hover:border-[#5B0A0D]/40 hover:text-[#5B0A0D] dark:hover:text-[#C87484]"
          >
            {listaAberta ? <ChevronUp className="h-3 w-3" /> : <ListChecks className="h-3 w-3" />}
            {listaAberta ? 'Fechar lista' : 'Mandar outra'}
          </button>
          {botaoNota}
        </div>
        {listaAberta && (
          <div className="space-y-2 border-t border-[#EDE8E0] pt-1.5 dark:border-[#332727]">
            <p className="text-[10.5px] text-[#5B0A0D] dark:text-[#C87484]">
              Cada mensagem aprovada é cobrada de novo. Com o cliente ainda sem responder, prefira esperar.
            </p>
            {escolha}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2 border-t border-border-warm bg-[#FAF8F4] px-3 py-2.5 dark:bg-[#241A1A]">
      <div className="flex items-center justify-between gap-2">
        <p className="flex min-w-0 flex-1 items-center gap-1.5 truncate text-[10px] text-muted-foreground">
          <Clock className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">
            <span className="font-semibold text-foreground">Janela de 24h fechada</span> — pela linha oficial só sai mensagem aprovada pela Meta. Quando o cliente responder, o texto livre volta
            {templates.length > 0 && <> · {liberadas} liberadas, {templates.length - liberadas} em análise</>}
            .
          </span>
        </p>
        {botaoNota}
      </div>
      {escolha}
    </div>
  );
}
